import React, { useEffect, useState } from 'react'
import { FormikValues } from 'formik'
import { FormikHelpers } from 'formik/dist/types'
import { useKeycloak } from 'react-keycloak'
import { get as _get } from 'lodash'
import { Skeleton } from '@material-ui/lab'
import { FormFeedback, FormGenerator } from '../../utils/formGenerator'
import {
    GetMeQuery,
    GetTenantQuery,
    TenantEfficiencyCalcSettingDto,
    useGetMeLazyQuery,
    useGetTenantLazyQuery,
    useUpdateTenantMutation,
} from '../../api/models'
import { createBaseContext } from '../../services/apiService'
import { extractError } from "../../utils/apiUtils";
import { StorageLocationConfig } from "./forms/storageLocationFormConfig";

export const StorageLocationTenantSetting = () => {
    const [keycloak] = useKeycloak()
    const [isLoading, setLoading] = useState(true)
    const [meRequest, meResponse] = useGetMeLazyQuery()
    const [updateTenantRequest] = useUpdateTenantMutation()
    const [tenantRequest, tenantResponse] = useGetTenantLazyQuery()
    const [initialValues, setInitialValues] = useState({
        zone: 0,
        rack: 0,
        level: 0,
        position: 0,
        section: 0,
    })

    useEffect(() => {
        if (!keycloak.authenticated) {
            keycloak.login()
        }
        if (keycloak.token) {
            meRequest(createBaseContext(keycloak.token || ''))
        }
    }, [keycloak, keycloak.token, meRequest])

    useEffect(() => {
        const payload = meResponse.data as GetMeQuery
        const tenantUuid = _get(payload, 'getMe.data.0.tenant.uuid', null)
        if (tenantUuid && keycloak.token) {
            tenantRequest(createBaseContext(keycloak.token, { id: tenantUuid }))
        }
    }, [meResponse.data, keycloak.token, tenantRequest])

    useEffect(() => {
        const payload = tenantResponse.data as GetTenantQuery
        if (_get(payload, 'getTenant.data', []).length > 0) {
            const calcSetting = _get(
                payload,
                'getTenant.data.0.config.storageLocationConfig',
                {}
            ) as TenantEfficiencyCalcSettingDto
            setInitialValues({
                zone: Math.trunc(_get(calcSetting, 'zone', 0) as number),
                rack: Math.trunc(_get(calcSetting, 'rack', 0) as number),
                level: Math.trunc(_get(calcSetting, 'level', 0) as number),
                position: Math.trunc(_get(calcSetting, 'position', 0) as number),
                section: Math.trunc(_get(calcSetting, 'section', 0) as number),
            })
            setTimeout(() => {
                setLoading(false)
            }, 300)
        }
    }, [tenantResponse.data])

    const onSubmit = (
        values: any,
        actions: FormikHelpers<FormikValues>,
        setFeedback: React.Dispatch<React.SetStateAction<FormFeedback>>
    ): void => {
        const tenantUuid = _get(meResponse, 'data.getMe.data.0.tenant.uuid', null)
        if (!keycloak.token) {
            return setFeedback({ type: 'error', message: 'Authentication issue. Please login again' })
        }
        const params = createBaseContext(keycloak.token, {
            id: tenantUuid,
            tenant: {
                config: {
                    storageLocationConfig: {
                        zone: values.zone,
                        rack: values.rack,
                        position: values.position,
                        section: values.section,
                        level: values.level
                    },
                },
            },
        })

        const submitForm = (): void => {
            updateTenantRequest(params)
                .then((response: any) => {
                    const error = extractError(response, 'updateTenant')
                    if (error) {
                        return setFeedback({
                            type: 'error',
                            message: `${_get(error, 'message', 'unknown')} (${_get(error, 'code', 'XXX')})`,
                        })
                    }
                    setFeedback({ type: 'success', message: 'Successfully changed the storage location config', timeout: 2500 })
                })
                .catch(err => {
                    setFeedback({ type: 'error', message: err })
                })
                .finally(() => {
                    actions.setSubmitting(false)
                })
        }
        setTimeout(submitForm, 1500)
    }

    if (isLoading) {
        return (
            <>
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
            </>
        )
    }

    return (
        <FormGenerator
            id="storage-loc-calc"
            config={StorageLocationConfig}
            submitLabel="Save"
            initialValues={initialValues}
            onSubmit={onSubmit}
        />
    )
}
